import React, { useState } from "react"
import { motion } from "framer-motion"
import {Users, Settings, Database, Image as ImageIcon, Upload, Building2, DollarSign, Download} from 'lucide-react'
import { useGuias } from "../context/GuiasContext"
import toast from "react-hot-toast"
import GerenciarUsuarios from "./GerenciarUsuarios"
import GerenciarTaxas from "./GerenciarTaxas"
import ExportarDados from "./ExportarDados"

export default function Administracao() {
  const { logoUrl, setLogoUrl, dadosPrefeitura, setDadosPrefeitura } = useGuias()
  const [previewLogo, setPreviewLogo] = useState(logoUrl)
  const [mostrarConfigLogo, setMostrarConfigLogo] = useState(false)
  const [mostrarGerenciarUsuarios, setMostrarGerenciarUsuarios] = useState(false)
  const [mostrarConfigPrefeitura, setMostrarConfigPrefeitura] = useState(false)
  const [mostrarGerenciarTaxas, setMostrarGerenciarTaxas] = useState(false)
  const [mostrarExportarDados, setMostrarExportarDados] = useState(false)
  const [dadosEditados, setDadosEditados] = useState(dadosPrefeitura || {
    nomePrefeitura: "",
    secretaria: "",
    departamento: "",
    endereco: "",
    cep: "",
    cnpj: "",
    telefone: "",
    chavePix: ""
  })

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validar tipo de arquivo
    if (!file.type.startsWith("image/")) {
      toast.error("Por favor, selecione um arquivo de imagem válido")
      return
    }

    // Validar tamanho (máx 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("A imagem deve ter no máximo 5MB")
      return
    }

    // Converter para base64
    const reader = new FileReader()
    reader.onloadend = () => {
      const base64String = reader.result as string
      setPreviewLogo(base64String)
      toast.success("Imagem carregada! Clique em 'Salvar Logo' para confirmar")
    }
    reader.onerror = () => {
      toast.error("Erro ao carregar a imagem")
    }
    reader.readAsDataURL(file)
  }

  const handleSalvarLogo = async () => {
    if (!previewLogo) {
      toast.error("Selecione uma imagem primeiro")
      return
    }
    await setLogoUrl(previewLogo)
    setMostrarConfigLogo(false)
  }

  const handleSalvarDadosPrefeitura = async () => {
    await setDadosPrefeitura(dadosEditados)
    setMostrarConfigPrefeitura(false)
  }

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-purple-500 rounded-lg">
            <Settings className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-800">Administração do Sistema</h3>
            <p className="text-sm text-gray-600">Gerencie usuários e configurações</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <motion.div
            whileHover={{ scale: 1.02 }}
            onClick={() => setMostrarConfigLogo(!mostrarConfigLogo)}
            className="p-6 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl border border-teal-200 cursor-pointer"
          >
            <ImageIcon className="w-10 h-10 text-teal-600 mb-4" />
            <h4 className="text-lg font-semibold text-gray-800 mb-2">Configurar Logo</h4>
            <p className="text-sm text-gray-600">
              Altere a logo exibida nas guias de sepultamento
            </p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            onClick={() => setMostrarConfigPrefeitura(!mostrarConfigPrefeitura)}
            className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-200 cursor-pointer"
          >
            <Building2 className="w-10 h-10 text-purple-600 mb-4" />
            <h4 className="text-lg font-semibold text-gray-800 mb-2">Dados da Prefeitura</h4>
            <p className="text-sm text-gray-600">
              Configure as informações exibidas nas guias
            </p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            onClick={() => setMostrarGerenciarUsuarios(!mostrarGerenciarUsuarios)}
            className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 cursor-pointer"
          >
            <Users className="w-10 h-10 text-blue-600 mb-4" />
            <h4 className="text-lg font-semibold text-gray-800 mb-2">Gerenciar Usuários</h4>
            <p className="text-sm text-gray-600">
              Adicione, edite ou remova usuários do sistema
            </p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            onClick={() => setMostrarGerenciarTaxas(!mostrarGerenciarTaxas)}
            className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200 cursor-pointer"
          >
            <DollarSign className="w-10 h-10 text-green-600 mb-4" />
            <h4 className="text-lg font-semibold text-gray-800 mb-2">Gerenciar Taxas</h4>
            <p className="text-sm text-gray-600">
              Configure os valores das taxas dos vendedores
            </p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            onClick={() => setMostrarExportarDados(!mostrarExportarDados)}
            className="p-6 bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl border border-orange-200 cursor-pointer"
          >
            <Download className="w-10 h-10 text-orange-600 mb-4" />
            <h4 className="text-lg font-semibold text-gray-800 mb-2">Exportar Dados</h4>
            <p className="text-sm text-gray-600">
              Exportar todas as colecções para migração ou backup
            </p>
          </motion.div>
        </div>

        {/* Configuração de Logo */}
        {mostrarConfigLogo && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 p-6 bg-gray-50 rounded-xl border border-gray-200"
          >
            <h4 className="text-lg font-semibold text-gray-800 mb-4">Configurar Logo do Município</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Logo Atual
                </label>
                <div className="flex items-center justify-center mb-4">
                  <img 
                    src={logoUrl} 
                    alt="Logo atual" 
                    className="h-32 w-auto object-contain border-2 border-gray-300 rounded-lg p-4 bg-white shadow-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload da Nova Logo
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-teal-500 transition-colors">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <label className="cursor-pointer">
                    <span className="text-teal-600 font-medium hover:text-teal-700">
                      Clique para fazer upload
                    </span>
                    <span className="text-gray-500"> ou arraste o arquivo aqui</span>
                    <input
                      type="file"
                      onChange={handleLogoUpload}
                      className="hidden"
                      accept="image/*"
                    />
                  </label>
                  <p className="text-xs text-gray-500 mt-2">
                    Formatos aceitos: PNG, JPG, JPEG, SVG (máx. 5MB)
                  </p>
                </div>
                {previewLogo !== logoUrl && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Preview da Nova Logo:</p>
                    <div className="flex items-center justify-center">
                      <img 
                        src={previewLogo} 
                        alt="Preview da nova logo" 
                        className="h-32 w-auto object-contain border-2 border-teal-300 rounded-lg p-4 bg-white shadow-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="flex gap-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleSalvarLogo}
                  className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                >
                  Salvar Logo
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setMostrarConfigLogo(false)}
                  className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancelar
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Configuração de Dados da Prefeitura */}
        {mostrarConfigPrefeitura && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 p-6 bg-gray-50 rounded-xl border border-gray-200"
          >
            <h4 className="text-lg font-semibold text-gray-800 mb-4">Configurar Dados da Prefeitura</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome da Prefeitura
                </label>
                <input
                  type="text"
                  value={dadosEditados?.nomePrefeitura || ""}
                  onChange={(e) => setDadosEditados({...dadosEditados, nomePrefeitura: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Secretaria
                </label>
                <input
                  type="text"
                  value={dadosEditados?.secretaria || ""}
                  onChange={(e) => setDadosEditados({...dadosEditados, secretaria: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Departamento
                </label>
                <input
                  type="text"
                  value={dadosEditados?.departamento || ""}
                  onChange={(e) => setDadosEditados({...dadosEditados, departamento: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Endereço
                </label>
                <input
                  type="text"
                  value={dadosEditados?.endereco || ""}
                  onChange={(e) => setDadosEditados({...dadosEditados, endereco: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CEP
                </label>
                <input
                  type="text"
                  value={dadosEditados?.cep || ""}
                  onChange={(e) => setDadosEditados({...dadosEditados, cep: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CNPJ
                </label>
                <input
                  type="text"
                  value={dadosEditados?.cnpj || ""}
                  onChange={(e) => setDadosEditados({...dadosEditados, cnpj: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telefone
                </label>
                <input
                  type="text"
                  value={dadosEditados?.telefone || ""}
                  onChange={(e) => setDadosEditados({...dadosEditados, telefone: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Chave PIX
                </label>
                <input
                  type="text"
                  value={dadosEditados?.chavePix || ""}
                  onChange={(e) => setDadosEditados({...dadosEditados, chavePix: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="email@prefeitura.gov.br"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleSalvarDadosPrefeitura}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Salvar Dados
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  setDadosEditados(dadosPrefeitura)
                  setMostrarConfigPrefeitura(false)
                }}
                className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancelar
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* Gerenciamento de Usuários */}
        {mostrarGerenciarUsuarios && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6"
          >
            <GerenciarUsuarios />
          </motion.div>
        )}

        {/* Gerenciamento de Taxas */}
        {mostrarGerenciarTaxas && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6"
          >
            <GerenciarTaxas />
          </motion.div>
        )}

        {/* Exportar Dados */}
        {mostrarExportarDados && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 p-6 bg-gray-50 rounded-xl border border-gray-200"
          >
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-gray-800">Exportar Todos os Dados</h4>
              <button
                onClick={() => setMostrarExportarDados(false)}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Fechar
              </button>
            </div>
            <ExportarDados />
          </motion.div>
        )}
      </motion.div>
    </div>
  )
}
